/**
 * NotificationBell - Wave 1 (2026-05-27).
 *
 * TopBar bell with unread badge, live-stream indicator (pulsing dot
 * when SSE is connected), and a click-to-open dropdown that previews
 * the 5 most-recent notifications. "See all" links to /notifications.
 *
 * The bell is a customer-grade trust signal - without it the product
 * feels like a static admin tool. With it, the SOC operator gets
 * pinged the moment a breach clock fires or a DDR rule blocks.
 */
import { useEffect, useState, useRef } from 'react';
import { Bell, CheckCheck, Settings, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LiveBadge } from '@/components/ui/live-badge';
import { useNotificationStream } from '@/lib/use-notification-stream';
import { useMotionLevel } from '@/lib/motion-level';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '@/lib/api';
import { cn } from '@/lib/cn';

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-destructive',
  high: 'bg-warning',
  medium: 'bg-info',
  info: 'bg-muted-foreground',
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const level = useMotionLevel();

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const res = await listNotifications({ limit: 8, includeRead: true });
      setItems(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      // silent - backend may not be ready for this tenant
    } finally {
      setLoading(false);
    }
  }

  // Initial load + refresh when dropdown opens.
  useEffect(() => {
    void refresh();
  }, []);
  useEffect(() => {
    if (open) void refresh();
  }, [open]);

  // SSE stream - prepend new items + bump counter.
  const { status: streamStatus } = useNotificationStream({
    onMessage: (n) => {
      setItems((prev) => [n, ...prev.filter((p) => p.id !== n.id)].slice(0, 8));
      if (n.unread) setUnreadCount((c) => c + 1);
    },
  });

  // Click-away + Escape close.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function handleMarkAll(): Promise<void> {
    await markAllNotificationsRead();
    setItems((prev) => prev.map((p) => ({ ...p, unread: false })));
    setUnreadCount(0);
  }

  async function handleItemClick(n: NotificationItem): Promise<void> {
    if (n.unread) {
      await markNotificationRead(n.id);
      setItems((prev) => prev.map((p) => (p.id === n.id ? { ...p, unread: false } : p)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    if (n.url) {
      window.location.href = n.url;
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-muted/40 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <motion.span
            initial={level === 'full' ? { scale: 0 } : { opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={level === 'full' ? { type: 'spring', stiffness: 400, damping: 22 } : { duration: 0.12 }}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground tabular"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={level === 'full' ? { opacity: 0, y: -6, scale: 0.98 } : { opacity: 0 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={level === 'full' ? { opacity: 0, y: -6, scale: 0.98 } : { opacity: 0 }}
            transition={level === 'full' ? { type: 'spring', stiffness: 360, damping: 28 } : { duration: 0.14 }}
            className="glass-strong absolute right-0 top-[calc(100%+8px)] z-50 w-[380px] rounded-xl shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Notifications</h3>
                <LiveBadge status={streamStatus === 'live' ? 'live' : streamStatus === 'connecting' ? 'connecting' : 'idle'} label="" />
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button
                    size="sm"
                    variant="glass"
                    data-action="disc.notifications.a06"
                    onClick={() => void handleMarkAll()}
                    title="Mark all as read"
                  >
                    <CheckCheck className="h-3 w-3" />
                  </Button>
                )}
                <Link
                  to="/admin/notification-preferences"
                  className="p-1.5 rounded hover:bg-muted/40"
                  title="Notification preferences"
                  onClick={() => setOpen(false)}
                >
                  <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded hover:bg-muted/40"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {loading && items.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-xs text-muted-foreground">You're all caught up.</p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {items.map((n) => (
                    <motion.button
                      key={n.id}
                      layout={level === 'full'}
                      initial={
                        level === 'full' ? { opacity: 0, y: -12, scale: 0.96 } : { opacity: 0 }
                      }
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={
                        level === 'full' ? { opacity: 0, x: 12, scale: 0.96 } : { opacity: 0 }
                      }
                      transition={
                        level === 'full'
                          ? { type: 'spring', stiffness: 360, damping: 28 }
                          : { duration: 0.14 }
                      }
                      onClick={() => void handleItemClick(n)}
                      className={cn(
                        'w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/30 hover:bg-muted/30 transition-colors',
                        n.unread && 'bg-primary/5',
                      )}
                    >
                    <span
                      className={cn(
                        'mt-1 h-2 w-2 shrink-0 rounded-full',
                        SEVERITY_DOT[n.severity] ?? 'bg-muted-foreground',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-xs leading-tight', n.unread && 'font-semibold')}>
                          {n.title}
                        </span>
                        <Badge variant="glass" className="text-[9px] uppercase shrink-0">
                          {n.source}
                        </Badge>
                      </div>
                      {n.body && (
                        <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2 leading-snug">
                          {n.body}
                        </p>
                      )}
                      <span className="mt-1 inline-block text-[10px] text-muted-foreground">
                        {timeAgo(n.emittedAt)}
                      </span>
                    </div>
                  </motion.button>
                  ))}
                </AnimatePresence>
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-border/40 text-center">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-primary hover:text-primary/80 underline underline-offset-2"
              >
                See all notifications
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
